import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import styles from './VideoPlayer.module.scss';
import { audio } from "services/audioManager";

// Dev-only: artificially hold the poster image for N ms before the video takes over.
// Useful for verifying the poster-hand-off is glitch-free on fast connections where
// the first frame would otherwise paint almost instantly. Set to 0 in production.
const DEV_POSTER_HOLD_MS = 0;

function shortSrc(src) {
    if (!src) return "(none)";
    return src.substring(src.lastIndexOf("/") + 1);
}

const VideoPlayer = forwardRef(({ video, show, onEnded, onError, onPlaybackStart, onPlay, onFirstFrame, onUpdateStill, className, posterImage: posterImageProp, showSkipButton = false, handleSlowLoad, forceMute = false, events = [], children }, ref) => {
    const videoRef = useRef(null);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [firstFrameLoaded, setFirstFrameLoaded] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isMutedByPlayer, setIsMutedByPlayer] = useState(false);
    const [usingFallbackAudio, setUsingFallbackAudio] = useState(false);
    const [updatedStill, setUpdatedStill] = useState(false);

    const spinnerTimeoutRef = useRef(null);
    const autoSkipTimeoutRef = useRef(null);
    const fallbackAudioPlayPromise = useRef(null);
    const eventsRef = useRef(events);
    const eventsFiredRef = useRef(new Set());
    const eventsRafRef = useRef(null);
    const playingRef = useRef(false);
    const maxRetries = 3;
    const autoplayBlockedRef = useRef(false);
    const duckRafRef = useRef(null);
    const duckMultiplierRef = useRef(audio.isDucked ? audio.duckLevel : 1);

    // Parse video prop to get src, fallbackAudio, and posterImage
    const videoSrc = typeof video === 'string' ? video : video?.src;
    const volume = typeof video === 'object' ? video?.volume ?? 0.7 : 0.7;
    const fallbackAudio = typeof video === 'object' ? video?.fallbackAudio : null;
    const fallbackAudioVolume = typeof fallbackAudio === 'object' ? fallbackAudio?.volume ?? 0.7 : 0.7;
    const fallbackAudioSrc = typeof fallbackAudio === 'string' ? fallbackAudio : fallbackAudio?.src;
    const posterImage = (typeof video === 'object' ? video?.posterImage : null) || posterImageProp;

    // Reset the poster/ended gates synchronously the moment the source changes,
    // BEFORE this render paints. These also get reset in the load effect below,
    // but that effect runs after paint — so on the first frame a new video is
    // shown, `firstFrameLoaded`/`hasEnded` would still be stale-true from the
    // previous (completed) video. That briefly hid the poster and let the bare
    // <video> paint a black/stale frame — the visible "blip" as a transition
    // starts. In Pointless a video only ever plays on a wrong answer, so that
    // blip tipped the result before the clip's own reveal could. Resetting here
    // keeps the poster (a neutral first-frame still) covering from frame one.
    const prevVideoSrcRef = useRef(videoSrc);
    if (prevVideoSrcRef.current !== videoSrc) {
        prevVideoSrcRef.current = videoSrc;
        if (firstFrameLoaded) setFirstFrameLoaded(false);
        if (hasEnded) setHasEnded(false);
    }

    const showOverlay = false;

    const SHOW_SPINNER_TIMEOUT_MS = 5000;
    const AUTO_SKIP_TIMEOUT_MS = 7000;

    useEffect(() => {
        const handleFirstInteraction = () => {
            if (autoplayBlockedRef.current && videoRef.current) {
                videoRef.current.play().catch(err => {
                    console.error('Still unable to play after interaction:', err);
                });
            }
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };

        document.addEventListener('click', handleFirstInteraction);
        document.addEventListener('keydown', handleFirstInteraction);

        return () => {
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };
    }, []);

    const handleError = (e) => {
        const vid = videoRef.current;

        // Handle play() promise rejection
        if (e instanceof Error) {
            if (e.name === 'NotAllowedError') {
                console.log('Autoplay blocked - muting video and using fallback audio');
                autoplayBlockedRef.current = true;

                // Mute video and use fallback audio if available
                if (vid) {
                    setIsMuted(true);
                    vid.muted = true;

                    // Start fallback audio using AudioManager
                    if (fallbackAudioSrc) {
                        setUsingFallbackAudio(true);
                        fallbackAudioPlayPromise.current = audio.playMusic(fallbackAudioSrc, {
                            volume: fallbackAudioVolume,
                            loop: false,
                            onEnd: () => {
                                console.log('Fallback audio ended');
                            }
                        });
                    }

                    vid.play().catch(err => {
                        console.error('Failed to play even when muted:', err);
                        const errorDetails = {
                            code: 5,
                            message: `Play error: ${err.message}`,
                            src: videoSrc,
                            networkState: vid?.networkState,
                            readyState: vid?.readyState
                        };
                        setError(errorDetails);
                        onError?.(errorDetails);
                    });
                }
                return;
            }

            // Other play errors
            const errorDetails = {
                code: 5,
                message: `Play error: ${e.message}`,
                src: videoSrc,
                networkState: vid?.networkState,
                readyState: vid?.readyState
            };

            console.error('Video Play Error:', errorDetails);
            setError(errorDetails);
            onError?.(errorDetails);
            return;
        }

        // Handle video element error events
        if (!vid || !videoSrc) return;

        setIsLoading(false);

        const errorDetails = {
            code: vid.error?.code,
            message: getErrorMessage(vid.error?.code),
            src: videoSrc,
            networkState: vid.networkState,
            readyState: vid.readyState
        };

        console.error('Video Error:', errorDetails);
        setError(errorDetails);

        if (retryCount < maxRetries) {
            console.log(`Retrying video load (${retryCount + 1}/${maxRetries})...`);
            setTimeout(() => {
                setRetryCount(prev => prev + 1);
                vid.load();
            }, 1000 * (retryCount + 1));
        } else {
            onError?.(errorDetails);
        }
    };

    const getErrorMessage = (code) => {
        switch (code) {
            case 1:
                return 'MEDIA_ERR_ABORTED: Video loading was aborted';
            case 2:
                return 'MEDIA_ERR_NETWORK: Network error occurred';
            case 3:
                return 'MEDIA_ERR_DECODE: Video decoding failed';
            case 4:
                return 'MEDIA_ERR_SRC_NOT_SUPPORTED: Video format not supported';
            default:
                return 'Unknown video error';
        }
    };

    useImperativeHandle(ref, () => ({
        skip: handleSkip,
        getError: () => error,
        isLoading: () => isLoading,
        isPlaying: () => isPlaying,
        isMuted: () => isMuted,
        usingFallbackAudio: () => usingFallbackAudio,
        setMute: (mute) => setIsMutedByPlayer(mute)
    }));

    const handleLoadedMetadata = () => {
        const v = videoRef.current;
        if (v) {
            // Explicitly seek to ensure first frame is rendered
            v.currentTime = 0;
        }
    };

    const handleLoadedData = () => {
    };

    const handleCanPlay = () => {
    };

    const handleCanPlayThrough = () => {
    };

    const handleStalled = () => {
    };

    const handleSuspend = () => {
    };

    const handleAbort = () => {
    };

    const handleProgress = () => {
    };

    const handleSeeked = () => {
        const v = videoRef.current;
        if (!v || firstFrameLoaded) return;

        // Wait for the frame to actually be painted to the screen
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const handoff = () => {
                    setFirstFrameLoaded(true);
                    onFirstFrame?.();

                    // Only start playing if show is true
                    if (videoRef.current && show) {
                        videoRef.current.play()
                            .catch(err => {
                                console.warn(`[VideoPlayer] play() rejected ${shortSrc(videoSrc)} | ${err?.name}: ${err?.message}`);
                                handleError(err);
                            });
                    }
                };

                if (DEV_POSTER_HOLD_MS > 0) {
                    setTimeout(handoff, DEV_POSTER_HOLD_MS);
                } else {
                    handoff();
                }
            });
        });
    };

    const handleEnded = () => {
        // Stop fallback audio if it's playing
        if (usingFallbackAudio && fallbackAudioPlayPromise.current?.handle) {
            fallbackAudioPlayPromise.current.handle.stop(100);
            fallbackAudioPlayPromise.current = null;
        }

        setHasEnded(true);
        onEnded();
    };

    const handlePlaying = () => {
        if (!videoSrc) return;
        clearTimeout(spinnerTimeoutRef.current);
        clearTimeout(autoSkipTimeoutRef.current);
        playingRef.current = true;
        setIsPlaying(true);
        setIsLoading(false);
        setShowSpinner(false);
        onPlaybackStart?.();
        onPlay?.();
    };

    const handleWaiting = () => {
        if (!videoSrc) return;
        if (videoRef.current) {
            const timeRemaining = videoRef.current.duration - videoRef.current.currentTime;
            if (timeRemaining <= 0) return;
        }
        setIsLoading(true);

        // Don't set up another chain if one is already running
        if (spinnerTimeoutRef.current) return;

        spinnerTimeoutRef.current = setTimeout(() => {
            setShowSpinner(true);
            handleSlowLoad?.();
            autoSkipTimeoutRef.current = setTimeout(() => {
                handleSkip();
            }, AUTO_SKIP_TIMEOUT_MS);
        }, SHOW_SPINNER_TIMEOUT_MS);
    };

    const handleLoadStart = () => {
        if (!videoSrc) return;
        setIsLoading(true);
        setIsPlaying(false);

        // Don't set up another chain if one is already running
        clearTimeout(spinnerTimeoutRef.current);
        clearTimeout(autoSkipTimeoutRef.current);
        spinnerTimeoutRef.current = null;
        autoSkipTimeoutRef.current = null;

        spinnerTimeoutRef.current = setTimeout(() => {
            setShowSpinner(true);
            handleSlowLoad?.();
            autoSkipTimeoutRef.current = setTimeout(() => {
                handleSkip();
            }, AUTO_SKIP_TIMEOUT_MS);
        }, SHOW_SPINNER_TIMEOUT_MS);
    };

    const handleSkip = () => {
        console.log('handleSkip - videoSrc:', videoSrc);

        // Stop fallback audio
        if (usingFallbackAudio && fallbackAudioPlayPromise.current?.handle) {
            fallbackAudioPlayPromise.current.handle.stop(100);
            fallbackAudioPlayPromise.current = null;
        }

        if (videoRef.current?.currentTime) {
            videoRef.current.currentTime = videoRef.current.duration;
        }
        onEnded();
    };

    // Pointless updates its still background once the video is a second in, so the
    // transition from still to video (and the reverse on the far side) is seamless.
    const handleTimeUpdate = () => {
        const v = videoRef.current;
        if (!v || updatedStill) return;

        if (v.currentTime >= 1) {
            setUpdatedStill(true);
            onUpdateStill?.();
        }
    };

    useEffect(() => {
        if (videoRef.current && videoSrc && show) {
            setError(null);
            setRetryCount(0);
            setIsLoading(true);
            setIsPlaying(false);
            setShowSpinner(false);
            setHasEnded(false);

            if (videoRef.current) {
                videoRef.current.muted = isMutedByPlayer || forceMute;
            }

            setFirstFrameLoaded(false);
            setUsingFallbackAudio(false);
            setUpdatedStill(false);
            playingRef.current = false;
            autoplayBlockedRef.current = false;
            clearTimeout(spinnerTimeoutRef.current);
            clearTimeout(autoSkipTimeoutRef.current);

            videoRef.current.volume = volume * duckMultiplierRef.current;

            // Stop any playing fallback audio
            if (fallbackAudioPlayPromise.current?.handle) {
                fallbackAudioPlayPromise.current.handle.stop(0);
                fallbackAudioPlayPromise.current = null;
            }

            videoRef.current.load();
        }

        // Pause when show becomes false. Don't call .load() — that resets the
        // <video> element to its default black background, which paints before
        // the parent's display:none takes effect and causes a black flash on
        // the post-video transition. Keeping src attached lets the element
        // hold its last frame in memory until the destination view paints.
        if (videoRef.current && !show) {
            videoRef.current.pause();

            // Stop fallback audio when hiding
            if (fallbackAudioPlayPromise.current?.handle) {
                fallbackAudioPlayPromise.current.handle.stop(100);
                fallbackAudioPlayPromise.current = null;
            }
        }

        return () => {
            clearTimeout(spinnerTimeoutRef.current);
            clearTimeout(autoSkipTimeoutRef.current);
            // Clean up fallback audio
            if (fallbackAudioPlayPromise.current?.handle) {
                fallbackAudioPlayPromise.current.handle.stop(0);
                fallbackAudioPlayPromise.current = null;
            }
            if (eventsRafRef.current) {
                cancelAnimationFrame(eventsRafRef.current);
                eventsRafRef.current = null;
            }
        };
    }, [videoSrc, show]);

    // Keep the element's muted property in sync. Set imperatively because the
    // React `muted` JSX attribute is not reliably applied across TV browsers.
    // Sources: isMuted (autoplay-block fallback), isMutedByPlayer (imperative
    // setMute), forceMute (Pointless SFX-mute prop).
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted || isMutedByPlayer || forceMute;
        }
    }, [isMuted, isMutedByPlayer, forceMute]);

    // Audio ducking — fade the video element's volume between full and ducked
    // when VO playback starts/ends. HTML video has no native fade so we ramp
    // via rAF over audio.duckFadeMs. No-op if the audio manager has no ducking.
    useEffect(() => {
        const rampVolumeTo = (targetMultiplier) => {
            if (duckRafRef.current) {
                cancelAnimationFrame(duckRafRef.current);
                duckRafRef.current = null;
            }
            const startMultiplier = duckMultiplierRef.current;
            if (startMultiplier === targetMultiplier) return;

            const fadeMs = audio.duckFadeMs ?? 300;
            const startTime = performance.now();

            const tick = () => {
                const elapsed = performance.now() - startTime;
                const t = Math.min(1, elapsed / fadeMs);
                const m = startMultiplier + (targetMultiplier - startMultiplier) * t;
                duckMultiplierRef.current = m;
                if (videoRef.current) {
                    try { videoRef.current.volume = volume * m; } catch { }
                }
                if (t < 1) {
                    duckRafRef.current = requestAnimationFrame(tick);
                } else {
                    duckRafRef.current = null;
                }
            };
            duckRafRef.current = requestAnimationFrame(tick);
        };

        const unsubscribe = audio.addDuckListener?.((ducked) => {
            rampVolumeTo(ducked ? (audio.duckLevel ?? 0.25) : 1);
        });

        return () => {
            if (duckRafRef.current) {
                cancelAnimationFrame(duckRafRef.current);
                duckRafRef.current = null;
            }
            unsubscribe?.();
        };
    }, [volume]);

    // Keep events ref fresh
    eventsRef.current = events;

    // Reset fired events when video source or visibility changes
    useEffect(() => {
        eventsFiredRef.current = new Set();
    }, [videoSrc, show]);

    // rAF event polling loop — reads currentTime each tick and fires
    // time-based events when their threshold is reached (fire-once via Set)
    useEffect(() => {
        if (!events || events.length === 0 || !show) return;

        const video = videoRef.current;
        if (!video) return;

        function tick() {
            eventsRafRef.current = requestAnimationFrame(tick);

            // Don't check events until playback has been confirmed
            if (!playingRef.current) return;

            const currentTime = video.currentTime;
            const currentEvents = eventsRef.current;

            for (let i = 0; i < currentEvents.length; i++) {
                const evt = currentEvents[i];
                if (currentTime >= evt.time && !eventsFiredRef.current.has(i)) {
                    eventsFiredRef.current.add(i);
                    try {
                        evt.handler();
                    } catch (e) {
                        console.warn("VideoPlayer event error:", e);
                    }
                }
            }
        }

        eventsRafRef.current = requestAnimationFrame(tick);

        return () => {
            if (eventsRafRef.current) {
                cancelAnimationFrame(eventsRafRef.current);
                eventsRafRef.current = null;
            }
        };
    }, [events, show, videoSrc]);

    return (
        <div className={`${styles.videoContainer} ${className || ''}`} data-show={show}>
            {posterImage && (
                <div className={styles.posterContainer} data-show={(!firstFrameLoaded) && show && !hasEnded}>
                    <img src={posterImage} alt="Video loading" className={styles.posterImage} />
                    {showSpinner && <div className={styles.loadingSpinner} />}
                </div>
            )}

            <video
                ref={videoRef}
                className={styles.videoPlayer}
                data-show={show}
                playsInline
                onEnded={handleEnded}
                onError={handleError}
                onPlaying={handlePlaying}
                onWaiting={handleWaiting}
                onLoadStart={handleLoadStart}
                onLoadedMetadata={handleLoadedMetadata}
                onLoadedData={handleLoadedData}
                onCanPlay={handleCanPlay}
                onCanPlayThrough={handleCanPlayThrough}
                onStalled={handleStalled}
                onSuspend={handleSuspend}
                onAbort={handleAbort}
                onProgress={handleProgress}
                onSeeked={handleSeeked}
                onTimeUpdate={handleTimeUpdate}
                muted={isMuted || isMutedByPlayer || forceMute}
                disableremoteplayback="true"
                controlsList="noremoteplayback"
                x-webkit-airplay="deny"
                playsinline="true"
                preload="metadata"
            >
                {videoSrc && <source src={videoSrc} type="video/mp4" />}
                Your browser does not support the video tag.
            </video>

            {children}

            {showSkipButton && (
                <button className={styles.skipButton} data-show={show && !error} onClick={handleSkip} aria-label="Skip video" >
                    Skip
                </button>
            )}

            {error && retryCount >= maxRetries && (
                <div className={styles.errorOverlay} data-show={show}>
                    <div className={styles.errorContent}>
                        <h3>Video Error</h3>
                        <p>{error.message}</p>
                        <div className={styles.errorButtons}>
                            <button onClick={() => {
                                setError(null);
                                setRetryCount(0);
                                videoRef.current?.load();
                            }}>
                                Try Again
                            </button>
                            <button onClick={handleSkip}>
                                Skip Video
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {
                showOverlay && (
                    <div className={styles.testOverlay} />
                )
            }
        </div>
    );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
